<template>
  <div class="editor-wrapper">
    <div v-if="!isMonacoLoaded" class="loading-message">
      Loading editor...
    </div>
    <div v-else :id="'editor-container-' + suffix"></div>
  </div>
</template>

<script>
import * as monaco from 'monaco-editor'

export default {
  name: 'MonacoEditorComponent',
  
  props: {
    value: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: 'javascript'
    },
    text_value: {
      type: String,
      default: ''
    },
    suffix: {
      type: String,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: "vs-dark"
    },
    options: {
      type: Object,
      default: () => ({})
    },
  },

  data() {
    return {
      editorInstance: null,
      internalReadonly: false,
      isMonacoLoaded: false
    }
  },

  watch: {
    readonly: {
      immediate: true,
      handler(newVal) {
        this.internalReadonly = newVal
        if (this.editorInstance) {
          this.editorInstance.updateOptions({ readOnly: this.internalReadonly })
        }
      }
    },
    text_value: {
      immediate: true,
      handler(newVal) {
        this.$nextTick(() => {
          if (this.editorInstance && newVal !== this.editorInstance.getValue()) {
            const model = this.editorInstance.getModel()
            if (model) {
              model.setValue(newVal || '')
            }
          } else if (!this.editorInstance && this.isMonacoLoaded && newVal) {
            // If editor not created yet but Monaco is loaded and we have content, initialize
            this.$nextTick(() => {
              this.initializeEditor()
            })
          }
        })
      }
    }
  },

  methods: {
    getValue() {
      return this.editorInstance ? this.editorInstance.getValue() : ''
    },

    async loadMonaco() {
      try {
        // Initialize Monaco
        monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
          noSemanticValidation: false,
          noSyntaxValidation: false,
        })

        monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
          target: monaco.languages.typescript.ScriptTarget.ES2020,
          allowNonTsExtensions: true,
          moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
          module: monaco.languages.typescript.ModuleKind.CommonJS,
          noEmit: true,
          esModuleInterop: true,
          jsx: monaco.languages.typescript.JsxEmit.React,
          allowJs: true,
          typeRoots: ["node_modules/@types"]
        })

        // Configure editor defaults
        monaco.editor.defineTheme('vs-dark', {
          base: 'vs-dark',
          inherit: true,
          rules: [],
          colors: {
            'editor.background': '#1E1E1E',
          }
        })

        monaco.editor.defineTheme('vs-light', {
          base: 'vs',
          inherit: true,
          rules: [],
         
        })

        // Make Monaco available globally
        window.monaco = monaco
        this.isMonacoLoaded = true
        return monaco
      } catch (error) {
        console.error('Failed to initialize Monaco:', error)
        throw error
      }
    },

    initializeEditor() {
      // Don't initialize if editor already exists
      if (this.editorInstance) {
        return
      }

      const element = document.getElementById('editor-container-' + this.suffix)
      if (!element) {
        console.error('Editor container not found for suffix:', this.suffix)
        return
      }

      // Check if element is visible (not hidden)
      if (element.offsetParent === null && !element.classList.contains('visible')) {
        // Element is hidden, wait a bit and try again
        setTimeout(() => {
          this.initializeEditor()
        }, 200)
        return
      }

      // Check if Monaco is loaded
      if (!this.isMonacoLoaded) {
        console.warn('Monaco not loaded yet, attempting to load...')
        this.loadMonaco().then(() => {
          this.$nextTick(() => {
            this.initializeEditor()
          })
        }).catch(error => {
          console.error('Failed to load Monaco:', error)
        })
        return
      }

      // Check if monaco is available globally
      if (typeof window.monaco === 'undefined' || !window.monaco || !window.monaco.editor) {
        console.error('Monaco editor not available. Window.monaco:', window.monaco)
        // Try to reload Monaco
        this.loadMonaco().then(() => {
          setTimeout(() => {
            this.initializeEditor()
          }, 200)
        })
        return
      }

      try {
        // Use window.monaco (which should be set during loadMonaco)
        const monacoInstance = window.monaco
        
        // Ensure Monaco editor API is available
        if (!monacoInstance || !monacoInstance.editor || typeof monacoInstance.editor.createModel !== 'function') {
          console.error('Monaco editor API not available. Monaco:', monacoInstance);
          return;
        }
        
        // Create editor with model
        const model = monacoInstance.editor.createModel(
          this.text_value || this.value || '',
          this.language
        )

        const defaultOptions = {
          model: model,
          theme: this.theme,
          readOnly: this.internalReadonly,
          folding: true,
          minimap: { enabled: false },
          scrollbar: {
            handleMouseWheel: true,
            alwaysConsumeMouseWheel: false,
          },
          scrollBeyondLastColumn: false,
          scrollBeyondLastLine: false,
          formatOnPaste: true,
          formatOnType: true,
          autoIndent: 'advanced',
          tabSize: 2,
          wordWrap: 'on',
          wrappingIndent: 'indent',
          automaticLayout: true,
          quickSuggestions: true,
          renderWhitespace: 'boundary',
          contextmenu: true,
          suggestOnTriggerCharacters: true,
          acceptSuggestionOnEnter: 'on',
          snippetSuggestions: 'inline',
          wordBasedSuggestions: true,
          parameterHints: {
            enabled: true
          }
        }

        this.editorInstance = monacoInstance.editor.create(element, {
          ...defaultOptions,
          ...this.options
        })

        // Add change event handler
        this.editorInstance.onDidChangeModelContent(() => {
          const value = this.editorInstance.getValue()
          this.$emit('input', value)
          this.$emit('change', value)
        })

        // Emit editor mounted event
        this.$emit('editorDidMount', this.editorInstance)

      } catch (error) {
        console.error('Error creating Monaco editor:', error)
      }
    }
  },

  async mounted() {
    try {
      //// Only auto-initialize if we have content and container is visible
      //if (this.text_value || this.value) {
      //  await this.loadMonaco()
      //  this.$nextTick(() => {
      //    // Give a small delay to ensure DOM is ready
      //    setTimeout(() => {
      //      this.initializeEditor()
      //    }, 100)
      //  })
      //} else {
      //  // Load Monaco but don't initialize editor yet (for lazy loading)
      //  await this.loadMonaco()
      //}
      await this.loadMonaco()
      this.$nextTick(() => {
        // Give a small delay to ensure DOM is ready
        setTimeout(() => {
          this.initializeEditor()
        }, 100)
      })

    } catch (error) {
      console.error('Failed to load Monaco editor:', error)
    }
  },
  
  updated() {
    // If editor isn't created yet but Monaco is loaded and we have content, initialize
    if (this.isMonacoLoaded && !this.editorInstance && (this.text_value || this.value)) {
      this.$nextTick(() => {
        const element = document.getElementById('editor-container-' + this.suffix)
        if (element && element.offsetParent !== null) {
          // Element is visible, safe to initialize
          this.initializeEditor()
        }
      })
    }
  },

  beforeDestroy() {
    if (this.editorInstance) {
      const model = this.editorInstance.getModel()
      if (model) {
        model.dispose()
      }
      this.editorInstance.dispose()
    }
  }
}
</script>

<style>
.editor-wrapper {
  display: flex;
  flex-direction: column;
  height: 60vh;
  margin: 0;
}

.editor-wrapper div {
  flex-grow: 1;
  width: 100%;
}

.loading-message {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  font-size: 1.2em;
}
</style>
